import { useEffect, useState } from "react";
import { io } from "socket.io-client";
import { motion } from "framer-motion";

const socket = io("https://warfc.com", {
  path: "/socket.io",
  reconnection: true,
  reconnectionAttempts: Infinity,
  timeout: 20000
});

export default function JudgePanel() {
  const params = new URLSearchParams(window.location.search);
  const ring = params.get("ring") || "A";
  const judgeId = Number(params.get("judge")) || 1;

  const [state, setState] = useState(null);
  const [selection, setSelection] = useState({});

  /* ---------------- OPTIONS ---------------- */
  const OPTIONS = [
    { label: "10 – 9", winner: "red", score: 9 },
    { label: "10 – 8", winner: "red", score: 8 },
    { label: "10 – 9", winner: "blue", score: 9 },
    { label: "10 – 8", winner: "blue", score: 8 },
    { label: "10 – 10", winner: "draw", score: null }
  ];

  const redOptions = OPTIONS.filter(o => o.winner === "red");
  const blueOptions = OPTIONS.filter(o => o.winner === "blue");
  const drawOption = OPTIONS.find(o => o.winner === "draw");

  /* ---------------- SOCKET SETUP ---------------- */
  useEffect(() => {
    socket.emit("join_ring", ring);

    const handleState = (data) => {
      if (data[ring]) setState(data[ring]);
    };

    socket.on("judging_state", handleState);
    return () => socket.off("judging_state", handleState);
  }, [ring]);

  if (!state) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-3xl font-bold">Connecting…</div>
      </div>
    );
  }

  const round = state.round;
  const roundData = state.rounds[round - 1] || {};
  const locked = roundData.locked;
  const currentSelection = selection[round] || null;

  /* ---------------- ACTIONS ---------------- */
  const submitScore = () => {
    if (!currentSelection || locked) return;

    socket.emit("judge_vote", {
      ring,
      judge: judgeId,
      round,
      winner: currentSelection.winner,
      score: currentSelection.score
    });

    setSelection(prev => ({ ...prev, [round]: null }));
  };

  const nextRound = () => {
    if (!state?.currentFight) return;

    if (state.phase === "round") {
      socket.emit("set_phase", {
        ring,
        phase: "rest",
        timer: state.currentFight.restDuration
      });
    } else {
      socket.emit("set_phase", {
        ring,
        phase: "round",
        timer: state.currentFight.roundDuration
      });
      socket.emit("next_round", { ring });
    }
  };

  /* ---------------- STYLES ---------------- */
  const getButtonStyle = (option) => {
    if (locked) return "bg-gray-800 opacity-50 cursor-not-allowed";

    const selected = 
      currentSelection?.label === option.label && 
      currentSelection?.winner === option.winner;

    if (option.winner === "red") {
      return selected
        ? "bg-red-600 border-4 border-yellow-400 scale-105"
        : "bg-red-700 hover:bg-red-600";
    }

    if (option.winner === "blue") {
      return selected
        ? "bg-blue-600 border-4 border-yellow-400 scale-105"
        : "bg-blue-700 hover:bg-blue-600";
    }

    return selected
      ? "bg-gray-400 text-black border-4 border-yellow-400 scale-105"
      : "bg-gray-600 hover:bg-gray-500";
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="min-h-screen bg-black text-white p-6 flex flex-col">
      <h1 className="text-3xl font-extrabold mb-1">
        Judge {judgeId} — Ring {ring}
      </h1>
      <div className="text-lg mb-4 opacity-80">Fight #{state.fightId}</div>

      <div className="text-8xl font-black text-yellow-400 mb-6">
        ROUND {round}
      </div>

      {locked && (
        <div className="mb-4 text-xl font-bold text-green-400">
          ROUND LOCKED – Waiting for next round
        </div>
      )}

      {/* SCORING */}
      <div className="flex flex-col flex-1 space-y-8">

        {/* RED + BLUE container */}
        <div className="flex justify-between gap-12">
          {/* RED */}
          <div className="flex flex-col space-y-4 w-52">
            <div className="text-center text-xl font-bold text-red-400">RED</div>
            {redOptions.map((o, i) => {
              const isSelected = currentSelection?.label === o.label && currentSelection?.winner === o.winner;
              return (
                <motion.button
                  key={i}
                  disabled={locked}
                  onClick={() => setSelection(prev => ({ ...prev, [round]: o }))}
                  animate={isSelected && !locked ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                  transition={isSelected ? { repeat: Infinity, duration: 0.7 } : { duration: 0 }}
                  className={`py-5 text-xl font-extrabold rounded transition-all ${getButtonStyle(o)}`}
                >
                  {o.label}
                </motion.button>
              );
            })}
          </div>

          {/* BLUE */}
          <div className="flex flex-col space-y-4 w-52">
            <div className="text-center text-xl font-bold text-blue-400">BLUE</div>
            {blueOptions.map((o, i) => {
              const isSelected = currentSelection?.label === o.label && currentSelection?.winner === o.winner;
              return (
                <motion.button
                  key={i}
                  disabled={locked}
                  onClick={() => setSelection(prev => ({ ...prev, [round]: o }))}
                  animate={isSelected && !locked ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                  transition={isSelected ? { repeat: Infinity, duration: 0.7 } : { duration: 0 }}
                  className={`py-5 text-xl font-extrabold rounded transition-all ${getButtonStyle(o)}`}
                >
                  {o.label}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* DRAW */}
        <div className="flex justify-center">
          <button
            disabled={locked}
            onClick={() =>
              setSelection(prev => ({ ...prev, [round]: drawOption }))
            }
            className={`w-64 py-4 text-xl font-extrabold rounded transition-all ${getButtonStyle(drawOption)}`}
          >
            {drawOption.label}
          </button>
        </div>

        {/* SUBMIT */}
        <div className="flex justify-center">
          <button
            onClick={submitScore}
            disabled={!currentSelection || locked}
            className="w-72 py-4 bg-green-600 disabled:bg-gray-700 text-2xl font-extrabold rounded"
          >
            SUBMIT ROUND
          </button>
        </div>

        {/* NEXT ROUND */}
        {locked && round < 3 && (
          <div className="flex justify-center">
            <button
              onClick={nextRound}
              className="w-72 py-4 bg-blue-600 hover:bg-blue-700 text-2xl font-extrabold rounded"
            >
              NEXT ROUND
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
